<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';
import { useAdmin } from 'dashboard/composables/useAdmin';
import MessageApi from 'dashboard/api/inbox/message';

import { EDIT_SYNC_STATUS, MESSAGE_STATUS, MESSAGE_TYPES } from './constants';
import { useMessageEditHistory } from 'dashboard/composables/messages/useMessageEditHistory';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();
const { isAdmin } = useAdmin();
const { openModal } = useMessageEditHistory();

const {
  isAnEmailChannel,
  isAWhatsAppChannel,
  isATwilioChannel,
  isAFacebookInbox,
  isASmsInbox,
  isATelegramChannel,
  isAnInstagramChannel,
  isAWebWidgetInbox,
  isAPIInbox,
  isALineChannel,
} = useInbox();

const {
  id,
  conversationId,
  status,
  editsSyncStatus,
  isPrivate,
  createdAt,
  scheduledAt,
  sourceId,
  messageType,
  contentAttributes,
  isMessageEdited,
  isMessageDeleted,
  content,
  attachments,
} = useMessageContext();

const isSyncPending = computed(
  () => editsSyncStatus.value === EDIT_SYNC_STATUS.PENDING
);
const isSyncFailed = computed(
  () => editsSyncStatus.value === EDIT_SYNC_STATUS.FAILED
);
const isRetrying = ref(false);

const retrySync = async () => {
  if (isRetrying.value) return;

  try {
    isRetrying.value = true;
    if (contentAttributes.value?.lastMessageAction === 'delete') {
      await MessageApi.retry_destroy(conversationId.value, id.value);
    } else {
      await MessageApi.retry_update(conversationId.value, id.value);
    }
  } catch {
    const errorMsg = isMessageDeleted.value
      ? t('CONVERSATION.DELETE_FAILED')
      : t('CONVERSATION.SYNC_FAILED');
    useAlert(errorMsg);
  } finally {
    isRetrying.value = false;
  }
};

const syncTooltip = computed(() => {
  const isDeleteAction =
    contentAttributes.value?.lastMessageAction === 'delete';
  if (isSyncPending.value) {
    return isDeleteAction
      ? t('CONVERSATION.DELETE_SYNCING')
      : t('CONVERSATION.EDIT_SYNCING');
  }
  if (isSyncFailed.value) {
    return isDeleteAction
      ? t('CONVERSATION.DELETE_FAILED_CLICK_TO_RETRY')
      : t('CONVERSATION.EDIT_FAILED_CLICK_TO_RETRY');
  }
  return '';
});

const readableTime = computed(() => {
  const timestamp =
    status.value === MESSAGE_STATUS.SCHEDULED && scheduledAt.value
      ? scheduledAt.value
      : createdAt.value;
  return messageTimestamp(timestamp, 'LLL d, h:mm a');
});

const hasChannelSourceId = computed(() => {
  const hasAttachmentSourceIds =
    isATelegramChannel.value &&
    attachments.value?.length > 0 &&
    attachments.value.every(attachment => !!attachment.sourceId);

  return !!sourceId.value || hasAttachmentSourceIds;
});

const showStatusIndicator = computed(() => {
  if (
    isPrivate.value ||
    status.value === MESSAGE_STATUS.FAILED ||
    contentAttributes.value?.deleted
  )
    return false;
  return [MESSAGE_TYPES.OUTGOING, MESSAGE_TYPES.TEMPLATE].includes(
    messageType.value
  );
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    [
      isAWhatsAppChannel,
      isATwilioChannel,
      isAFacebookInbox,
      isASmsInbox,
      isATelegramChannel,
      isAnInstagramChannel,
    ].some(c => c.value)
  ) {
    return hasChannelSourceId.value && status.value === MESSAGE_STATUS.SENT;
  }
  return isALineChannel.value;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    [
      isAWhatsAppChannel,
      isATwilioChannel,
      isASmsInbox,
      isAFacebookInbox,
      isATelegramChannel,
    ].some(c => c.value)
  ) {
    return (
      hasChannelSourceId.value && status.value === MESSAGE_STATUS.DELIVERED
    );
  }
  return isAWebWidgetInbox.value || isAPIInbox.value
    ? status.value === MESSAGE_STATUS.SENT
    : status.value === MESSAGE_STATUS.DELIVERED;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;
  if (
    [
      isAWhatsAppChannel,
      isATwilioChannel,
      isAFacebookInbox,
      isAnInstagramChannel,
      isAWebWidgetInbox,
      isAPIInbox,
    ].some(c => c.value)
  ) {
    return isAWebWidgetInbox.value || isAPIInbox.value
      ? status.value === MESSAGE_STATUS.READ
      : sourceId.value && status.value === MESSAGE_STATUS.READ;
  }
  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;
  return MESSAGE_STATUS.PROGRESS;
});

const toHistoryAttachment = a => ({
  id: a.id,
  file_url: a.dataUrl || '',
  file_name: (a.dataUrl || '').substring(
    (a.dataUrl || '').lastIndexOf('/') + 1
  ),
  content_type: a.fileType,
  file_size: a.fileSize,
});

const openEditHistory = () => {
  openModal(id.value, conversationId.value, {
    content: content.value,
    attachments: (attachments.value || []).map(toHistoryAttachment),
  });
};
</script>

<template>
  <div class="text-xs flex items-center gap-1.5">
    <Icon
      v-if="(isMessageEdited || isMessageDeleted) && isAdmin"
      v-tooltip="t('CONVERSATION.VIEW_MESSAGE_HISTORY')"
      icon="i-lucide-history"
      class="size-3 cursor-pointer text-slate-500 hover:text-slate-700"
      @click.stop="openEditHistory"
    />

    <template v-if="isMessageEdited || isMessageDeleted">
      <Icon
        v-if="isMessageDeleted"
        v-tooltip="t('CONVERSATION.MESSAGE_DELETED')"
        icon="i-lucide-trash-2"
        class="size-3 text-red-500"
      />
      <Icon
        v-else
        v-tooltip="t('CONVERSATION.MESSAGE_EDITED')"
        icon="i-lucide-edit"
        class="size-3 text-slate-400"
      />
    </template>

    <div
      v-if="isSyncPending || isSyncFailed || isRetrying"
      class="flex items-center"
    >
      <button
        v-if="isSyncFailed || isRetrying"
        v-tooltip="syncTooltip"
        class="hover:text-red-700 transition-colors flex items-center p-0 m-0 border-none bg-transparent"
        :class="isSyncFailed && !isRetrying ? 'text-red-500' : 'text-slate-400'"
        @click.stop="retrySync"
      >
        <Icon
          :icon="isRetrying ? 'i-lucide-loader-2' : 'i-lucide-alert-triangle'"
          class="size-3"
          :class="{ 'animate-spin': isRetrying }"
        />
      </button>

      <div
        v-else-if="isSyncPending"
        v-tooltip="syncTooltip"
        class="text-slate-400 flex items-center"
      >
        <Icon icon="i-lucide-loader-2" class="size-3 animate-spin" />
      </div>
    </div>

    <div class="inline">
      <time class="inline text-slate-500">{{ readableTime }}</time>
    </div>

    <Icon
      v-if="isPrivate"
      icon="i-lucide-lock-keyhole"
      class="size-3 text-slate-400"
    />

    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
